import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';

declare var google: any;

export interface MapEvent {
  type: 'drag' | 'dragstart' | 'dragend' | 'click' | 'zoom_changed' | 'center_changed' | 'idle';
  data?: any;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

@Injectable({
  providedIn: 'root'
})
export class MapEventsService {
  private mapEvents$ = new Subject<MapEvent>();
  private eventListeners: Map<string, google.maps.MapsEventListener[]> = new Map();

  /**
   * Observable pour écouter les événements de carte
   */
  get events$(): Observable<MapEvent> {
    return this.mapEvents$.asObservable();
  }

  /**
   * Configure les événements de base pour une carte
   */
  setupMapEvents(map: google.maps.Map, options: {
    enableDrag?: boolean;
    enableZoom?: boolean;
    enableClick?: boolean;
  } = {}): void {
    const { enableDrag = true, enableZoom = true, enableClick = true } = options;
    const listeners: google.maps.MapsEventListener[] = [];

    if (enableDrag) {
      listeners.push(
        map.addListener('drag', () => this.emitEvent('drag')),
        map.addListener('dragstart', () => this.emitEvent('dragstart')),
        map.addListener('dragend', () => this.emitEvent('dragend'))
      );
    }

    if (enableZoom) {
      listeners.push(
        map.addListener('zoom_changed', () => this.emitEvent('zoom_changed')),
        map.addListener('center_changed', () => this.emitEvent('center_changed'))
      );
    }

    if (enableClick) {
      listeners.push(
        map.addListener('click', (event: any) => this.emitEvent('click', event))
      );
    }

    listeners.push(
      map.addListener('idle', () => this.emitEvent('idle'))
    );

    this.eventListeners.set('map', listeners);
  }

  /**
   * Configure les événements pour un marqueur
   */
  setupMarkerEvents(marker: google.maps.Marker, markerId: string): void {
    const listeners: google.maps.MapsEventListener[] = [];

    listeners.push(
      marker.addListener('click', (event: any) => {
        this.emitEvent('click', { marker, event });
      }),
      marker.addListener('drag', (event: any) => {
        this.emitEvent('drag', { marker, event });
      }),
      marker.addListener('dragend', (event: any) => {
        this.emitEvent('dragend', { marker, event });
      })
    );

    this.eventListeners.set(`marker-${markerId}`, listeners);
  }

  /**
   * Émet un événement
   */
  private emitEvent(type: MapEvent['type'], data?: any): void {
    this.mapEvents$.next({ type, data });
  }

  /**
   * Nettoie tous les événements
   */
  clearAllEvents(): void {
    this.eventListeners.forEach(listeners => {
      listeners.forEach(listener => {
        google.maps.event.removeListener(listener);
      });
    });
    this.eventListeners.clear();
  }

  /**
   * Nettoie les événements d'un élément spécifique
   */
  clearEvents(elementId: string): void {
    const listeners = this.eventListeners.get(elementId);
    if (listeners) {
      listeners.forEach(listener => {
        google.maps.event.removeListener(listener);
      });
      this.eventListeners.delete(elementId);
    }
  }

  /**
   * Obtient les limites actuelles de la carte
   */
  getMapBounds(map: google.maps.Map): MapBounds | null {
    try {
      const bounds = map.getBounds();
      if (bounds) {
        return {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng(),
        };
      }
    } catch (error) {
      console.error('Error getting map bounds:', error);
    }
    return null;
  }

  /**
   * Vérifie si une position est dans les limites de la carte
   */
  isPositionInBounds(
    position: { lat: number; lng: number },
    bounds: MapBounds
  ): boolean {
    return (
      position.lat >= bounds.south &&
      position.lat <= bounds.north &&
      position.lng >= bounds.west &&
      position.lng <= bounds.east
    );
  }

  /**
   * Calcule la distance entre deux points
   */
  calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const lat1 = point1.lat * Math.PI / 180;
    const lat2 = point2.lat * Math.PI / 180;
    const deltaLat = (point2.lat - point1.lat) * Math.PI / 180;
    const deltaLng = (point2.lng - point1.lng) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // Rayon de la Terre en mètres
    const R = 6371000;
    return R * c;
  }

  /**
   * Formate une distance en unités lisibles
   */
  formatDistance(meters: number): string {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      return `${(meters / 1000).toFixed(1)}km`;
    }
  }

  /**
   * Obtient le centre de la carte
   */
  getMapCenter(map: google.maps.Map): { lat: number; lng: number } | null {
    try {
      const center = map.getCenter();
      if (center) {
        return {
          lat: center.lat(),
          lng: center.lng(),
        };
      }
    } catch (error) {
      console.error('Error getting map center:', error);
    }
    return null;
  }

  /**
   * Obtient le niveau de zoom de la carte
   */
  getMapZoom(map: google.maps.Map): number | null {
    try {
      const zoom = map.getZoom();
      return zoom !== undefined ? zoom : null;
    } catch (error) {
      console.error('Error getting map zoom:', error);
      return null;
    }
  }
} 